import { createApi } from '@reduxjs/toolkit/query/react';
import { newBackendAxiosBaseQuery } from '../../utils/axiosBaseQuery';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';
import {
    AvailableCodeDoorAsset,
    CodeDoor,
    CodeDoorCode,
    CreateCodeDoorRequest,
    UpdateCodeDoorRequest,
} from './accessCodeDoorApiTypes';

// Code-door management on the new (ECS) backend — the admin producer surface
// for `static_code` access points, consumed by the object detail page's
// AccessCodeSection. Tenant-scoped via the shared X-Tenant header and NOT
// entitlement-gated: a static code is baseline, not the paid add-on
// (ADR-0001 Amendment 3). Self-service only — a legacy store answers 409,
// which is why the section is not offered for legacy tenants. Backend:
// #1660, `new-backend/admin/src/routes/codeDoorRoutes.ts` at
// `/admin/code-doors`.
export const accessCodeDoorApiSlice = createApi({
    reducerPath: 'accessCodeDoorApiSlice',
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: newBackendAxiosBaseQuery(),
    tagTypes: ['CodeDoors', 'CodeDoorAssets'],
    endpoints: (builder) => ({
        getCodeDoors: builder.query<CodeDoor[], void>({
            query: () => ({ url: '/code-doors', method: 'GET' }),
            providesTags: ['CodeDoors'],
        }),
        // Assets a new code door can attach to (1:1 guard, plan A4). Backend
        // owns eligibility — the client never guesses from group data.
        getAvailableCodeDoorAssets: builder.query<AvailableCodeDoorAsset[], void>({
            query: () => ({ url: '/code-doors/available-assets', method: 'GET' }),
            providesTags: ['CodeDoorAssets'],
        }),
        // The code is a secret: reveal is its own endpoint (event-logged
        // server-side) and the result is never cached (`keepUnusedDataFor: 0`).
        revealCodeDoorCode: builder.query<CodeDoorCode, { id: string }>({
            query: ({ id }) => ({
                url: `/code-doors/${encodeURIComponent(id)}/code`,
                method: 'GET',
            }),
            keepUnusedDataFor: 0,
        }),
        createCodeDoor: builder.mutation<CodeDoor, CreateCodeDoorRequest>({
            query: (body) => ({ url: '/code-doors', method: 'POST', data: body }),
            invalidatesTags: ['CodeDoors', 'CodeDoorAssets'],
        }),
        updateCodeDoorCode: builder.mutation<CodeDoor, UpdateCodeDoorRequest>({
            query: ({ id, code }) => ({
                url: `/code-doors/${encodeURIComponent(id)}`,
                method: 'PATCH',
                data: { code },
            }),
            invalidatesTags: ['CodeDoors'],
        }),
        // Generic block (plan A9): sets asset.state and stops code serving.
        setCodeDoorBlocked: builder.mutation<CodeDoor, { id: string, blocked: boolean }>({
            query: ({ id, blocked }) => ({
                url: `/code-doors/${encodeURIComponent(id)}/${blocked ? 'block' : 'unblock'}`,
                method: 'POST',
            }),
            invalidatesTags: ['CodeDoors'],
        }),
        archiveCodeDoor: builder.mutation<void, { id: string }>({
            query: ({ id }) => ({
                url: `/code-doors/${encodeURIComponent(id)}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['CodeDoors', 'CodeDoorAssets'],
        }),
    }),
});

export const {
    useGetCodeDoorsQuery,
    useGetAvailableCodeDoorAssetsQuery,
    useLazyRevealCodeDoorCodeQuery,
    useCreateCodeDoorMutation,
    useUpdateCodeDoorCodeMutation,
    useSetCodeDoorBlockedMutation,
    useArchiveCodeDoorMutation,
} = accessCodeDoorApiSlice;
